import React, { useEffect, KeyboardEvent } from 'react';

import styled from '@emotion/styled';
import { createPortal } from 'react-dom';

import {
  disableBackgroundScrolling,
  enableBackgroundScrolling,
  isServer
} from 'src/utils/common';

import * as style from './ModalStyle';
import Spinner from '../Spinner';

interface StyledProps {
  closeIconColor?: string;
  separateCloseIcon?: boolean;
  padding: string;
  margin?: string;
  isLoading?: boolean;
  handleResponseForSm?: boolean;
  handleResponseForMobileXs?: boolean;
  border?: string;
  overflow?: string;
  maxHeight?: string;
  isCommercePopup?: boolean;
  removeBackdrop?: boolean;
}

interface ModalProps extends StyledProps {
  show: boolean;
  onClose?: () => void;
  children: JSX.Element;
  width: string;
  height: string;
  background: string;
  renderCloseIcon: boolean;
  cssClassName?: string;
}

const ModalOverLay = styled.div`
  ${(props: Partial<StyledProps>) => style.modalOverlay(props)}
`;
const ModalContainer = styled.div`
  ${(props: Partial<StyledProps>) => style.modalContainer(props)}
`;
const ModalHeader = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  ${(props: Partial<StyledProps>) => style.modalHeader(props)}
`;
const ModalBody = styled.div`
  ${(props: Partial<StyledProps>) => style.modalBody(props)}
`;

const Modal = ({
  show,
  onClose,
  isLoading,
  children,
  width,
  height,
  background,
  renderCloseIcon,
  closeIconColor,
  separateCloseIcon = false,
  padding,
  margin,
  handleResponseForSm = false,
  handleResponseForMobileXs = false,
  border,
  overflow,
  removeBackdrop,
  isCommercePopup,
  maxHeight,
  cssClassName = ''
}: ModalProps) => {
  // styles
  useEffect(() => {
    if (show && !isServer()) {
      disableBackgroundScrolling();
      return () => enableBackgroundScrolling();
    }
  }, [show]);

  const handleClose = () => {
    onClose && onClose();
  };

  // Close modal when user clicks esc button
  const handleKeyDown = (event: KeyboardEvent<HTMLImageElement>) => {
    if (event.key === 'Escape') {
      onClose && onClose();
    }
  };

  if (!isServer() && show) {
    return createPortal(
      <ModalOverLay
        data-test-id="modalOverLay"
        removeBackdrop={removeBackdrop}
        className="drowerPopupContent"
        onKeyDown={handleKeyDown}>
        <ModalContainer
          data-test-id="modalContainer"
          {...{
            handleResponseForSm,
            handleResponseForMobileXs,
            width,
            height,
            background,
            margin,
            border,
            overflow,
            maxHeight,
            cssClassName,
            isCommercePopup
          }}>
          {isLoading && <Spinner />}
          {renderCloseIcon && (
            <ModalHeader
              data-test-id="modalHeader"
              {...{
                closeIconColor,
                separateCloseIcon,
                handleResponseForMobileXs
              }}>
              <svg
                data-testid="close-btn"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                css={style.closeButton}
                onClick={handleClose}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </ModalHeader>
          )}
          <ModalBody
            data-test-id="modalBody"
            {...{
              padding,
              maxHeight,
              cssClassName,
              isCommercePopup
            }}>
            {children}
          </ModalBody>
        </ModalContainer>
      </ModalOverLay>,
      document.getElementById('modal-root') as Element | DocumentFragment
    );
  } else {
    return null;
  }
};

export default React.memo(Modal);
